import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2, Smartphone, Monitor, Square, Film, Music4, Subtitles } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";

const FORMATS = [
  { id: "native", label: "Original", sub: "auto", icon: Film },
  { id: "9:16", label: "Vertical", sub: "9:16", icon: Smartphone },
  { id: "16:9", label: "Horizontal", sub: "16:9", icon: Monitor },
  { id: "1:1", label: "Cuadrado", sub: "1:1", icon: Square },
];
const QUALITIES = ["HD 720p", "Full HD 1080p", "4K"];

export default function ExportPanel({ projects }) {
  const ready = projects.filter((p) => p.status === "ready");
  const readySongs = ready.filter((p) => p.type === "song");
  const [selId, setSelId] = useState(ready[0]?.id || "");
  const [format, setFormat] = useState("native");
  const [quality, setQuality] = useState("Full HD 1080p");
  const [soundtrack, setSoundtrack] = useState("");
  const [burnSubs, setBurnSubs] = useState(false);
  const [subSize, setSubSize] = useState("medium");
  const [subColor, setSubColor] = useState("white");
  const [subPosition, setSubPosition] = useState("bottom");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const selProject = projects.find((p) => p.id === selId);
  const isVideoType = selProject && (selProject.type === "video" || selProject.type === "shortfilm");
  const isShortfilm = selProject && selProject.type === "shortfilm";

  const doExport = async () => {
    if (!selId) { toast.error("Selecciona un proyecto listo"); return; }
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`/projects/${selId}/export`, {
        soundtrack_project_id: isVideoType && soundtrack ? soundtrack : null,
        aspect: isVideoType ? format : "native",
        quality,
        burn_subtitles: isShortfilm ? burnSubs : false,
        sub_size: subSize,
        sub_color: subColor,
        sub_position: subPosition,
      });
      setResult(r.data);
      toast.success("Exportación lista");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo exportar");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 fade-up">
      <div className="flex items-center gap-3">
        <Download className="w-6 h-6 text-[#FF334B]" />
        <h2 className="font-head text-2xl sm:text-3xl font-bold tracking-tight">Exportar</h2>
      </div>

      <div className="mfp-card p-6 space-y-6">
        <div>
          <label className="mono-label">Proyecto</label>
          <select data-testid="export-project-select" value={selId} onChange={(e) => { setSelId(e.target.value); setResult(null); }}
            className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B]">
            <option value="">Selecciona un proyecto</option>
            {ready.map((p) => <option key={p.id} value={p.id}>{p.title} · {p.type}</option>)}
          </select>
        </div>

        {isVideoType && (
          <div>
            <label className="mono-label flex items-center gap-2"><Music4 className="w-3.5 h-3.5" /> Banda sonora</label>
            <select data-testid="export-soundtrack-select" value={soundtrack} onChange={(e) => setSoundtrack(e.target.value)}
              className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#FF334B]">
              <option value="">Sin banda sonora</option>
              {readySongs.map((s) => <option key={s.id} value={s.id}>{s.title}</option>)}
            </select>
            <p className="text-xs text-[#64748B] mt-2">Junta una canción que ya generaste para que suene de fondo en el video.</p>
          </div>
        )}

        {isVideoType && (
          <div>
            <label className="mono-label">Formato</label>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {FORMATS.map((f) => (
                <button key={f.id} data-testid={`export-format-${f.id}`} onClick={() => setFormat(f.id)}
                  className={`p-4 rounded-xl border flex flex-col items-center gap-2 transition-colors ${format === f.id ? "border-[#FF334B] bg-[#FF334B]/10" : "border-white/12 hover:border-white/25"}`}>
                  <f.icon className={`w-6 h-6 ${format === f.id ? "text-[#FF334B]" : "text-[#94A3B8]"}`} />
                  <span className="text-sm font-medium">{f.label}</span>
                  <span className="mono-label">{f.sub}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {isShortfilm && (
          <div data-testid="subtitle-options">
            <button type="button" data-testid="toggle-burn-subs" onClick={() => setBurnSubs(!burnSubs)}
              className={`w-full text-left p-4 rounded-xl border transition-colors ${burnSubs ? "border-[#FF9F2E] bg-[#FF9F2E]/10" : "border-white/12 hover:border-white/25"}`}>
              <div className="flex items-center justify-between">
                <span className="font-head font-semibold text-sm flex items-center gap-2"><Subtitles className="w-4 h-4 text-[#FF9F2E]" /> Incrustar subtítulos</span>
                <span className={`w-9 h-5 rounded-full flex items-center px-0.5 transition-colors ${burnSubs ? "bg-[#FF9F2E] justify-end" : "bg-white/15"}`}>
                  <span className="w-4 h-4 rounded-full bg-white" />
                </span>
              </div>
              <p className="text-xs text-[#64748B] mt-1">Graba la narración de cada escena como subtítulos dentro del MP4.</p>
            </button>
            {burnSubs && (
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="mono-label">Tamaño</label>
                  <select data-testid="sub-size-select" value={subSize} onChange={(e) => setSubSize(e.target.value)}
                    className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#FF334B]">
                    <option value="small">Pequeño</option>
                    <option value="medium">Mediano</option>
                    <option value="large">Grande</option>
                  </select>
                </div>
                <div>
                  <label className="mono-label">Color</label>
                  <select data-testid="sub-color-select" value={subColor} onChange={(e) => setSubColor(e.target.value)}
                    className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#FF334B]">
                    <option value="white">Blanco</option>
                    <option value="yellow">Amarillo</option>
                  </select>
                </div>
                <div>
                  <label className="mono-label">Posición</label>
                  <select data-testid="sub-position-select" value={subPosition} onChange={(e) => setSubPosition(e.target.value)}
                    className="mt-2 w-full bg-[#0B0C13] border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#FF334B]">
                    <option value="bottom">Abajo</option>
                    <option value="center">Centro</option>
                    <option value="top">Arriba</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        )}

        <div>
          <label className="mono-label">Calidad</label>
          <div className="mt-3 flex flex-wrap gap-2">
            {QUALITIES.map((q) => (
              <button key={q} data-testid={`export-quality-${q}`} onClick={() => setQuality(q)}
                className={`px-3.5 py-2 rounded-full text-sm font-medium border transition-colors ${quality === q ? "bg-[#FF334B] border-[#FF334B] text-white" : "border-white/12 text-[#94A3B8] hover:border-[#FF334B]/50"}`}>{q}</button>
            ))}
          </div>
        </div>

        <button data-testid="timeline-export-button" onClick={doExport} disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#FF334B] hover:bg-[#E0223A] text-white font-semibold py-3.5 rounded-full transition-colors disabled:opacity-60">
          {busy ? <Loader2 className="w-5 h-5 spin" /> : <Download className="w-5 h-5" />}
          {busy ? "Exportando…" : "Exportar"}
        </button>

        {result?.download_path && (
          <div className="mfp-elev rounded-xl p-5 text-center fade-up" data-testid="export-result">
            <p className="text-emerald-400 font-semibold mb-3">✅ Exportación lista{isShortfilm && burnSubs ? " · con subtítulos incrustados" : ""}</p>
            {result.kind === "audio"
              ? <audio src={mediaUrl(result.download_path)} controls className="w-full mb-4" />
              : <video src={mediaUrl(result.download_path)} controls className="w-full rounded-lg bg-black mb-4" />}
            <a data-testid="export-download-link" href={mediaUrl(result.download_path)} download
              className="inline-flex items-center gap-2 bg-white text-black font-semibold px-6 py-3 rounded-full hover:bg-white/90 transition-colors">
              <Download className="w-4 h-4" /> Descargar archivo
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
